<template>
  <!-- FE-4 · one line on the brand card: how much of the GEO site is live, and what needs a hand.
       No pages and no answer (the switch is off, the read failed) → nothing drawn. -->
  <router-link v-if="summary && summary.total" class="bpl" :to="PAGES_HOME" :aria-label="ariaLabel">
    <span class="bpl-lab">{{ t("pages_card_lab") }}</span>
    <span class="bpl-v">{{ line }}</span>
    <span class="bpl-go" aria-hidden="true">›</span>
  </router-link>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

import { PAGES_HOME } from "../api/pages.js";  // FE-5 · one place decides where "all pages" is
import { cachedPagesSummary, loadPagesSummary } from "../stores/pages.js";
import { useI18n } from "../useI18n";

const props = defineProps({ brandId: { type: [Number, String], default: null } });

const { t } = useI18n();
const summary = ref(null);
let controller;

function count(key, n) {
  return t(n === 1 ? `${key}_one` : key, { n });
}

const line = computed(() => {
  if (!summary.value) return "";
  const parts = [count("pages_ov_live", summary.value.live)];
  if (summary.value.needRefresh) parts.push(count("pages_ov_refresh", summary.value.needRefresh));
  else if (summary.value.unanswered) parts.push(count("pages_ov_gap", summary.value.unanswered));
  return parts.join(" · ");
});

const ariaLabel = computed(() => `${t("pages_card_lab")}: ${line.value}`);

async function load() {
  summary.value = cachedPagesSummary(props.brandId);
  if (!props.brandId) return;
  controller?.abort();
  controller = new AbortController();
  // Never an error line here: this is an extra on somebody else's card, and a brand card that
  // turns into a red banner because one optional read failed is a worse card.
  const fresh = await loadPagesSummary(props.brandId, { signal: controller.signal });
  if (fresh) summary.value = fresh;
}

onMounted(load);
watch(() => props.brandId, load);
onBeforeUnmount(() => controller?.abort());
</script>

<style scoped>
.bpl {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 0;
  min-height: 32px;
  border-top: 1px solid var(--separator);
  color: var(--body);
  font-size: var(--fs-caption);
}

.bpl:hover .bpl-v {
  text-decoration: underline;
}

.bpl-lab {
  font-weight: 700;
  color: var(--ink);
}

.bpl-v {
  flex: 1 1 auto;
  font-variant-numeric: tabular-nums;
}

.bpl-go {
  color: var(--indigo);
  font-weight: 700;
}

.bpl:focus-visible {
  outline: 3px solid var(--indigo);
  outline-offset: 2px;
  border-radius: 8px;
}
</style>
